// ✅ src/components/screens/BuyerProfileScreen.js (FULL - UPDATED)
// ✅ CHANGE: Removed Tracking code / ETA / Carrier section completely
// ✅ Keeps: progress bar + steps + updates timeline (tracking still works)

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { auth, db } from "../../lib/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

/* ---------------- helpers ---------------- */
function safe(v) {
  return String(v ?? "").trim();
}
function initials(name) {
  const s = safe(name);
  if (!s) return "U";
  const parts = s.split(" ").filter(Boolean);
  const a = parts[0]?.[0] || "U";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}
function firstName(name, fallback = "User") {
  const t = safe(name);
  if (!t) return fallback;
  return t.split(" ").filter(Boolean)[0] || fallback;
}
function moneyGHS(n) {
  const num = Number(n || 0);
  if (Number.isNaN(num)) return "GHS 0";
  return `GHS ${num.toLocaleString()}`;
}
function fmtDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}
function shortId(id) {
  const t = safe(id);
  if (!t) return "—";
  return t.length > 8 ? `…${t.slice(-8)}` : t;
}
function normalizePhoneForWhatsApp(phoneRaw) {
  const p0 = safe(phoneRaw).replace(/\s+/g, "");
  if (!p0) return "";
  const digits = p0.replace(/[^\d]/g, "");
  if (digits.startsWith("233") && digits.length >= 12) return digits;
  if (digits.startsWith("0") && digits.length === 10) return "233" + digits.slice(1);
  return digits;
}

/* ✅ Ensure users/{uid} exists for BUYER */
async function ensureBuyerUserDoc(u) {
  if (!u?.uid) return;

  const ref = doc(db, "users", u.uid);

  try {
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      await setDoc(ref, {
        uid: u.uid,
        displayName: safe(u.displayName) || safe(u.email)?.split("@")[0] || "User",
        email: safe(u.email),
        photoURL: safe(u.photoURL),

        role: "BUYER",
        isBanned: false,

        defaultAddressId: "",
        phone: "",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return;
    }

    await setDoc(
      ref,
      {
        displayName: safe(u.displayName) || safe(u.email)?.split("@")[0] || "User",
        email: safe(u.email),
        photoURL: safe(u.photoURL),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (e) {
    console.log("ensureBuyerUserDoc failed:", e);
  }
}

/* ---------------- tracking mapping ---------------- */
const TRACK_STEPS = ["PLACED", "CONFIRMED", "PACKED", "OUT_FOR_DELIVERY", "DELIVERED"];

function stepTitle(step) {
  switch (step) {
    case "PLACED":
      return "Order placed";
    case "CONFIRMED":
      return "Confirmed";
    case "PACKED":
      return "Packed";
    case "OUT_FOR_DELIVERY":
      return "Out for delivery";
    case "DELIVERED":
      return "Delivered";
    default:
      return safe(step) || "—";
  }
}
function statusToStep(status) {
  const s = safe(status).toUpperCase();
  if (s === "NEW") return "PLACED";
  if (s === "PENDING_CONFIRMATION") return "PLACED";
  if (s === "CONFIRMED") return "CONFIRMED";
  if (s === "PACKED") return "PACKED";
  if (s === "OUT_FOR_DELIVERY") return "OUT_FOR_DELIVERY";
  if (s === "DELIVERED" || s === "DONE") return "DELIVERED";
  return "PLACED";
}
function statusBadge(status) {
  const s = safe(status).toUpperCase();
  if (s === "NEW") return { t: "NEW", cls: "bNew" };
  if (s === "PENDING_CONFIRMATION") return { t: "PENDING", cls: "bPending" };
  if (s === "CONFIRMED") return { t: "CONFIRMED", cls: "bOk" };
  if (s === "DONE" || s === "DELIVERED") return { t: "DONE", cls: "bDone" };
  return { t: s || "—", cls: "bGray" };
}

/* ✅ shared storeId detection (used by Orders + Support)
   ✅ IMPORTANT: forcedStoreId (from URL) ALWAYS wins.
*/
async function detectActiveStoreId(user, forcedStoreId = "") {
  // ✅ 0) forced storeId wins (URL /me?storeId=...)
  const forced = safe(forcedStoreId);
  if (forced) return forced;

  // 1) localStorage (fast)
  const a =
    safe(localStorage.getItem("activeStoreId")) ||
    safe(localStorage.getItem("active_store_id")) ||
    safe(localStorage.getItem("lastStoreId")) ||
    safe(localStorage.getItem("last_store_id"));

  if (a) return a;

  // 2) latest order: try multiple possible field names
  const uid = safe(user?.uid) || safe(auth.currentUser?.uid);
  if (uid) {
    try {
      const q0 = query(
        collection(db, "orders"),
        where("buyerId", "==", uid),
        orderBy("createdAt", "desc"),
        limit(1)
      );
      const snap0 = await getDocs(q0);
      const d0 = snap0.docs?.[0];
      const data = d0 ? d0.data() : null;

      const sid =
        safe(data?.storeId) ||
        safe(data?.storeUid) ||
        safe(data?.shopId) ||
        safe(data?.store?.id) ||
        safe(data?.store?.uid) ||
        safe(data?.store?.storeId) ||
        safe(data?.sellerStoreId);

      if (sid) return sid;
    } catch {}
  }

  // 3) fallback: try from last saved item (device)
  try {
    const raw = localStorage.getItem("wishlist_items_v1");
    const parsed = raw ? JSON.parse(raw) : [];
    const arr = Array.isArray(parsed) ? parsed : [];
    const last = arr?.[0] || arr?.[arr.length - 1] || null;
    const sid =
      safe(last?.storeId) ||
      safe(last?.store?.id) ||
      safe(last?.store) ||
      safe(last?.storeUid) ||
      safe(last?.shopId) ||
      "";
    return sid;
  } catch {
    return "";
  }
}

/* ---------------- main screen ---------------- */
export default function BuyerProfileScreen({ goBack }) {
  const [params] = useSearchParams();
  const forcedStoreId = safe(params.get("storeId")); // ✅ store context from URL

  const [user, setUser] = useState(() => auth.currentUser);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profile, setProfile] = useState(null);
  const [msg, setMsg] = useState("");

  const [active, setActive] = useState("overview");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      if (u) await ensureBuyerUserDoc(u);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoadingProfile(true);
      setMsg("");
      setProfile(null);

      const u = auth.currentUser;
      if (!u) {
        if (alive) setLoadingProfile(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        const data = snap.exists() ? snap.data() : null;
        if (!alive) return;
        setProfile(data);
      } catch (e) {
        if (!alive) return;
        setMsg(e?.message || "Failed to load profile.");
      } finally {
        if (alive) setLoadingProfile(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [user]);

  const displayName = useMemo(() => {
    const a = safe(user?.displayName);
    if (a) return a;
    const em = safe(user?.email);
    if (em) return em.split("@")[0];
    return "User";
  }, [user]);

  const photoURL = safe(user?.photoURL);
  const email = safe(user?.email);

  const phone = safe(profile?.phone);
  const defaultAddressId = safe(profile?.defaultAddressId);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      goBack?.();
    } catch (e) {
      setMsg(e?.message || "Logout failed.");
    }
  };

  const selectTab = (key) => {
    setActive(key);
    setDrawerOpen(false);
  };

  return (
    <div className="bp">
      <style>{css}</style>

      {/* Topbar */}
      <div className="top">
        <div className="topInner">
          <button
            className="ghost ham"
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            ☰
          </button>

          <button className="ghost back" type="button" onClick={goBack}>
            ← Back
          </button>

          <div className="brand">
            <div className="dot" />
            <div className="brandText">My Account</div>
          </div>

          <div className="topRight">
            {user ? (
              <button className="danger" type="button" onClick={handleLogout}>
                Logout
              </button>
            ) : (
              <span />
            )}
          </div>
        </div>
      </div>

      <div className="wrap">
        <div className="wrapInner">
          {!user ? (
            <div className="panel">
              <div className="h1">You’re not signed in</div>
              <div className="p">Go back and tap “Sign up”.</div>
              <button className="primary" type="button" onClick={goBack}>
                Go back
              </button>
            </div>
          ) : (
            <div className="layout">
              <aside className="side desktopSide">
                <Sidebar
                  active={active}
                  onSelect={selectTab}
                  photoURL={photoURL}
                  displayName={displayName}
                  email={email}
                  onLogout={handleLogout}
                />
              </aside>

              {drawerOpen ? (
                <div className="drawerWrap" role="dialog" aria-modal="true">
                  <div className="drawerOverlay" onClick={() => setDrawerOpen(false)} />
                  <div className="drawer">
                    <div className="drawerTop">
                      <div className="drawerTitle">Menu</div>
                      <button
                        className="ghost"
                        type="button"
                        onClick={() => setDrawerOpen(false)}
                      >
                        ✕
                      </button>
                    </div>

                    <Sidebar
                      active={active}
                      onSelect={selectTab}
                      photoURL={photoURL}
                      displayName={displayName}
                      email={email}
                      onLogout={handleLogout}
                      isMobile
                    />
                  </div>
                </div>
              ) : null}

              <main className="main">
                {active === "overview" ? (
                  <OverviewPanel
                    loading={loadingProfile}
                    msg={msg}
                    displayName={displayName}
                    email={email}
                    phone={phone}
                    defaultAddressId={defaultAddressId}
                    onGo={(key) => selectTab(key)}
                  />
                ) : active === "orders" ? (
                  <OrdersPanel user={user} profile={profile} forcedStoreId={forcedStoreId} />
                ) : active === "saved" ? (
                  <SavedPanel />
                ) : active === "addresses" ? (
                  <AddressesPanel user={user} profile={profile} />
                ) : (
                  <SupportPanel user={user} forcedStoreId={forcedStoreId} />
                )}
              </main>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- sidebar ---------------- */
function Sidebar({ active, onSelect, photoURL, displayName, email, onLogout, isMobile }) {
  return (
    <div className={`sideCard ${isMobile ? "mobileCard" : ""}`}>
      <div className="miniUser">
        <div className="miniAvatar">
          {photoURL ? <img src={photoURL} alt="profile" /> : <span>{initials(displayName)}</span>}
        </div>
        <div className="miniMeta">
          <div className="miniName">Hi, {firstName(displayName)}</div>
          <div className="miniSub">{email || "—"}</div>
        </div>
      </div>

      <div className="nav">
        <SideItem active={active === "overview"} label="Overview" icon="🏠" onClick={() => onSelect("overview")} />
        <SideItem active={active === "orders"} label="My Orders" icon="📦" onClick={() => onSelect("orders")} />
        <SideItem active={active === "saved"} label="Saved Items" icon="❤️" onClick={() => onSelect("saved")} />
        <SideItem active={active === "addresses"} label="Addresses" icon="📍" onClick={() => onSelect("addresses")} />
        <SideItem active={active === "support"} label="Support" icon="💬" onClick={() => onSelect("support")} />
      </div>

      <button className="logoutSide" type="button" onClick={onLogout}>
        ⏻ Logout
      </button>
    </div>
  );
}
function SideItem({ active, label, icon, onClick }) {
  return (
    <button className={`sideItem ${active ? "active" : ""}`} type="button" onClick={onClick}>
      <span className="sideIcon">{icon}</span>
      <span className="sideLabel">{label}</span>
      <span className="chev">›</span>
    </button>
  );
}

/* ---------------- overview ---------------- */
function OverviewPanel({ loading, msg, displayName, email, phone, defaultAddressId, onGo }) {
  return (
    <>
      <div className="hero">
        <div>
          <div className="title">Account Overview</div>
          <div className="subtitle">Your account, orders, saved items, addresses.</div>
        </div>
        <button className="primary" type="button" onClick={() => onGo("orders")}>
          View Orders
        </button>
      </div>

      <div className="panel">
        <div className="panelTop">
          <div>
            <div className="h1">Profile</div>
            <div className="p">Your basic account information.</div>
          </div>
          {loading ? <div className="pill">Loading…</div> : <div className="pill ok">Active</div>}
        </div>

        {msg ? <div className="alert">{msg}</div> : null}

        <div className="profileGrid">
          <Info label="Full name" value={displayName} />
          <Info label="Email" value={email || "—"} />
          <Info label="Phone" value={phone || "—"} />
          <Info label="Default address" value={defaultAddressId ? defaultAddressId : "Not set"} />
        </div>

        <div className="actions">
          <button className="soft" type="button" onClick={() => onGo("orders")}>
            Orders + Tracking
          </button>
          <button className="soft" type="button" onClick={() => onGo("saved")}>
            Saved Items
          </button>
          <button className="soft" type="button" onClick={() => onGo("addresses")}>
            Addresses
          </button>
        </div>
      </div>
    </>
  );
}
function Info({ label, value }) {
  return (
    <div className="info">
      <div className="infoLabel">{label}</div>
      <div className="infoValue">{value}</div>
    </div>
  );
}

/* ---------------- ORDERS + TRACKING ---------------- */
function OrdersPanel({ user, profile, forcedStoreId }) {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [err, setErr] = useState("");

  const [storeId, setStoreId] = useState("");

  const [mode, setMode] = useState("list"); // list | detail | track
  const [selected, setSelected] = useState(null);

  const [itemsLoading, setItemsLoading] = useState(false);
  const [items, setItems] = useState([]);

  const buyerUid = safe(user?.uid);
  const buyerPhone = safe(profile?.phone);

  useEffect(() => {
    let alive = true;

    async function loadOrders() {
      setLoading(true);
      setErr("");
      setOrders([]);

      // ✅ store context fixed: URL storeId wins
      const sid = await detectActiveStoreId(user, forcedStoreId);
      if (!alive) return;
      setStoreId(sid);

      try {
        let got = [];

        if (buyerUid) {
          // ✅ Best case: buyerId + storeId
          if (sid) {
            try {
              const q0 = query(
                collection(db, "orders"),
                where("buyerId", "==", buyerUid),
                where("storeId", "==", sid),
                orderBy("createdAt", "desc"),
                limit(50)
              );
              const snap0 = await getDocs(q0);
              got = snap0.docs.map((d) => ({ id: d.id, ...d.data() }));
            } catch {}
          }

          // fallback: buyerId only then filter client-side by sid
          if (!got.length) {
            try {
              const q1 = query(
                collection(db, "orders"),
                where("buyerId", "==", buyerUid),
                orderBy("createdAt", "desc"),
                limit(50)
              );
              const snap1 = await getDocs(q1);
              const raw = snap1.docs.map((d) => ({ id: d.id, ...d.data() }));

              got = sid
                ? raw.filter((o) => {
                    const osid =
                      safe(o?.storeId) ||
                      safe(o?.storeUid) ||
                      safe(o?.shopId) ||
                      safe(o?.store?.id) ||
                      safe(o?.store?.uid) ||
                      safe(o?.sellerStoreId);
                    return safe(osid) === safe(sid);
                  })
                : raw;
            } catch {}
          }

          // fallback: buyer.uid schema
          if (!got.length) {
            try {
              const q2 = query(
                collection(db, "orders"),
                where("buyer.uid", "==", buyerUid),
                orderBy("createdAt", "desc"),
                limit(50)
              );
              const snap2 = await getDocs(q2);
              const raw = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));

              got = sid
                ? raw.filter((o) => {
                    const osid =
                      safe(o?.storeId) ||
                      safe(o?.store?.id) ||
                      safe(o?.storeUid) ||
                      safe(o?.shopId) ||
                      safe(o?.sellerStoreId);
                    return safe(osid) === safe(sid);
                  })
                : raw;
            } catch {}
          }
        }

        // ✅ fallback: phone (uses sid; NOT stale state)
        if (!got.length && buyerPhone) {
          try {
            const q3 = query(
              collection(db, "orders"),
              where("buyer.phone", "==", buyerPhone),
              orderBy("createdAt", "desc"),
              limit(50)
            );
            const snap3 = await getDocs(q3);
            const raw = snap3.docs.map((d) => ({ id: d.id, ...d.data() }));

            got = sid
              ? raw.filter((o) => {
                  const osid =
                    safe(o?.storeId) ||
                    safe(o?.storeUid) ||
                    safe(o?.shopId) ||
                    safe(o?.store?.id) ||
                    safe(o?.store?.uid) ||
                    safe(o?.sellerStoreId);
                  return safe(osid) === safe(sid);
                })
              : raw;
          } catch {}
        }

        if (!alive) return;
        setOrders(got);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load orders.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadOrders();
    return () => {
      alive = false;
    };
  }, [buyerUid, buyerPhone, user, forcedStoreId]);

  const openDetail = async (o) => {
    setSelected(o);
    setMode("detail");
    setItems([]);
    setItemsLoading(true);

    try {
      const snap = await getDocs(collection(db, "orders", o.id, "items"));
      const it = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(it);
    } catch {
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const openTracking = async (o) => {
    try {
      const snap = await getDoc(doc(db, "orders", o.id));
      const fresh = snap.exists() ? { id: snap.id, ...snap.data() } : o;
      setSelected(fresh);
    } catch {
      setSelected(o);
    }
    setMode("track");
  };

  const backToList = () => {
    setMode("list");
    setSelected(null);
    setItems([]);
    setErr("");
  };

  return (
    <div className="panel">
      <div className="panelTop">
        <div>
          <div className="h1">My Orders</div>
          <div className="p">
            {storeId ? (
              <>
                Showing orders for this store: <b>{storeId}</b>
              </>
            ) : (
              <>Showing all your orders (no active store detected).</>
            )}
          </div>
        </div>

        {mode !== "list" ? (
          <button className="soft" type="button" onClick={backToList}>
            ← Back to orders
          </button>
        ) : null}
      </div>

      {err ? <div className="alert">{err}</div> : null}

      {loading ? (
        <div className="empty">Loading orders…</div>
      ) : mode === "list" ? (
        <>
          {!orders.length ? (
            <div className="empty">📦 No orders yet for this store.</div>
          ) : (
            <div className="orderGrid">
              {orders.map((o) => {
                const st = statusBadge(o.status);
                const total = Number(o.total ?? o.subtotal ?? 0);
                const trackStep =
                  safe(o?.tracking?.currentStep) || statusToStep(o?.status);

                return (
                  <div className="orderCard" key={o.id}>
                    <div className="orderTop">
                      <div className="orderId">Order {shortId(o.id)}</div>
                      <span className={`badge ${st.cls}`}>{st.t}</span>
                    </div>

                    <div className="orderRow">
                      <div className="muted2">Date</div>
                      <div className="val">{fmtDate(o.createdAt)}</div>
                    </div>

                    <div className="orderRow">
                      <div className="muted2">Total</div>
                      <div className="val">{moneyGHS(total)}</div>
                    </div>

                    <div className="orderRow">
                      <div className="muted2">Tracking</div>
                      <div className="val">{stepTitle(trackStep)}</div>
                    </div>

                    <div className="orderActions">
                      <button
                        className="primarySm"
                        type="button"
                        onClick={() => openDetail(o)}
                      >
                        View
                      </button>
                      <button
                        className="softSm"
                        type="button"
                        onClick={() => openTracking(o)}
                        title="Track order"
                      >
                        Track
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : mode === "detail" ? (
        <OrderDetail
          selected={selected}
          items={items}
          itemsLoading={itemsLoading}
          onTrack={openTracking}
        />
      ) : (
        <OrderTracking selected={selected} />
      )}
    </div>
  );
}

function OrderDetail({ selected, items, itemsLoading, onTrack }) {
  if (!selected) return <div className="empty">Select an order.</div>;

  const total = Number(selected.total ?? selected.subtotal ?? 0);
  const deliveryType = safe(selected?.delivery?.type) || "—";
  const deliveryFee = Number(selected?.delivery?.fee ?? 0);
  const buyerName = safe(selected?.buyer?.name) || "—";
  const buyerPhone = safe(selected?.buyer?.phone) || "—";

  return (
    <div style={{ marginTop: 12 }}>
      <div className="h2">Order {shortId(selected.id)}</div>
      <div className="p">Details + items.</div>

      <div className="detailGrid">
        <DetailBox label="Total" value={moneyGHS(total)} />
        <DetailBox label="Status" value={safe(selected.status) || "—"} />
        <DetailBox label="Delivery" value={deliveryType} />
        <DetailBox label="Delivery fee" value={moneyGHS(deliveryFee)} />
        <DetailBox label="Buyer name" value={buyerName} />
        <DetailBox label="Buyer phone" value={buyerPhone} />
      </div>

      <div className="subPanel">
        <div className="panelTop" style={{ marginBottom: 10 }}>
          <div>
            <div className="h1">Items</div>
            <div className="p">Snapshot items saved in order.</div>
          </div>
          <button className="soft" type="button" onClick={() => onTrack(selected)}>
            Track this order
          </button>
        </div>

        {itemsLoading ? (
          <div className="empty">Loading items…</div>
        ) : !items.length ? (
          <div className="empty">No items found in this order.</div>
        ) : (
          <div className="itemsList">
            {items.map((it) => (
              <div className="itemRow" key={it.id}>
                <div className="itemTitle">{safe(it.titleSnapshot) || "Item"}</div>
                <div className="itemMeta">
                  <span>{moneyGHS(it.priceAtTime)}</span>
                  <span>× {Number(it.qty || 1)}</span>
                  {Number(it.discountPercentAtTime || 0) ? (
                    <span className="badge bPending">-{Number(it.discountPercentAtTime)}%</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
function DetailBox({ label, value }) {
  return (
    <div className="detailBox">
      <div className="infoLabel">{label}</div>
      <div className="infoValue">{value}</div>
    </div>
  );
}

/* ✅ Tracking screen (REMOVED trackingCode / ETA / Carrier section) */
function OrderTracking({ selected }) {
  if (!selected) return <div className="empty">Select an order to track.</div>;

  const tracking = selected?.tracking || null;
  const current = safe(tracking?.currentStep) || statusToStep(selected?.status);

  const idx = Math.max(0, TRACK_STEPS.indexOf(current));
  const progress =
    TRACK_STEPS.length <= 1 ? 0 : (idx / (TRACK_STEPS.length - 1)) * 100;

  const history = Array.isArray(tracking?.history) ? tracking.history : [];

  return (
    <div style={{ marginTop: 12 }}>
      <div className="h2">Tracking</div>
      <div className="p">Delivery progress for Order {shortId(selected.id)}.</div>

      <div className="trackCard">
        {/* ✅ ONLY progress bar + steps */}
        <div className="barWrap">
          <div className="bar">
            <div className="barFill" style={{ width: `${progress}%` }} />
          </div>
          <div className="barLabels">
            {TRACK_STEPS.map((s) => (
              <span
                key={s}
                className={`barLabel ${TRACK_STEPS.indexOf(s) <= idx ? "on" : ""}`}
                title={stepTitle(s)}
              >
                {stepTitle(s)}
              </span>
            ))}
          </div>
        </div>

        <div className="timeline">
          <div className="h1">Updates</div>
          <div className="p">Latest at the top.</div>

          {history.length ? (
            <div className="timelineList">
              {[...history]
                .sort((a, b) => {
                  const ta = a?.at?.toDate ? a.at.toDate().getTime() : 0;
                  const tb = b?.at?.toDate ? b.at.toDate().getTime() : 0;
                  return tb - ta;
                })
                .map((h, i) => (
                  <div className="timeRow" key={`${i}-${h.step}`}>
                    <div className="dot2" />
                    <div>
                      <div className="timeTitle">{stepTitle(h.step)}</div>
                      <div className="muted2">{safe(h.text) || "Update"}</div>
                      <div className="muted2">{fmtDate(h.at)}</div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="empty">No timeline updates yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- SAVED ITEMS (localStorage) ---------------- */
function SavedPanel() {
  const [items, setItems] = useState([]);

  const load = () => {
    try {
      const raw = localStorage.getItem("wishlist_items_v1");
      const parsed = raw ? JSON.parse(raw) : [];
      setItems(Array.isArray(parsed) ? parsed : []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const removeOne = (id) => {
    const pid = safe(id);
    const next = items.filter((x) => safe(x?.id) !== pid);
    setItems(next);
    localStorage.setItem("wishlist_items_v1", JSON.stringify(next));
  };

  return (
    <div className="panel">
      <div className="panelTop">
        <div>
          <div className="h1">Saved Items</div>
          <div className="p">Your wishlist saved on this device.</div>
        </div>
        <button className="soft" type="button" onClick={load}>
          Refresh
        </button>
      </div>

      {!items.length ? (
        <div className="empty">❤️ No saved items yet.</div>
      ) : (
        <div className="savedGrid">
          {items.map((it, idx) => {
            const img =
              safe(it?.imageUrl) ||
              safe(it?.coverUrl) ||
              safe(it?.photoUrl) ||
              safe(it?.thumbUrl) ||
              safe(it?.image) ||
              "";

            return (
              <div className="savedCard" key={safe(it?.id) || idx}>
                <div className="savedTop">
                  <div className="savedImg">
                    {img ? (
                      <img src={img} alt={safe(it?.title) || "item"} />
                    ) : (
                      <div className="imgPh">No image</div>
                    )}
                  </div>

                  <div className="savedInfo">
                    <div className="savedTitle">{safe(it?.title) || "Item"}</div>
                    <div className="muted2">{safe(it?.storeName) || "—"}</div>
                    <div className="val">
                      {(safe(it?.currency) || "GHS") +
                        " " +
                        Number(it?.price || 0).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="savedBottom">
                  <button
                    className="dangerSm"
                    type="button"
                    onClick={() => removeOne(it?.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- ADDRESSES ---------------- */
function AddressesPanel({ user, profile }) {
  const uid = safe(user?.uid);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [err, setErr] = useState("");

  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    label: "",
    fullName: safe(profile?.displayName) || safe(user?.displayName) || "",
    phone: safe(profile?.phone) || "",
    region: "",
    city: "",
    addressLine1: "",
    addressLine2: "",
    landmark: "",
    isDefault: true,
  });

  const load = async () => {
    setLoading(true);
    setErr("");
    setList([]);
    try {
      const q1 = query(
        collection(db, "addresses"),
        where("userId", "==", uid),
        orderBy("createdAt", "desc"),
        limit(50)
      );
      const snap = await getDocs(q1);
      setList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setErr(e?.message || "Failed to load addresses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!uid) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const save = async () => {
    setErr("");
    try {
      const payload = {
        ...form,
        userId: uid,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      };

      const ref = await addDoc(collection(db, "addresses"), payload);

      if (form.isDefault) {
        try {
          await updateDoc(doc(db, "users", uid), {
            defaultAddressId: ref.id,
            updatedAt: serverTimestamp(),
          });
        } catch {}
      }

      setShowForm(false);
      setForm({
        label: "",
        fullName: safe(profile?.displayName) || safe(user?.displayName) || "",
        phone: safe(profile?.phone) || "",
        region: "",
        city: "",
        addressLine1: "",
        addressLine2: "",
        landmark: "",
        isDefault: true,
      });

      await load();
    } catch (e) {
      setErr(e?.message || "Failed to save address.");
    }
  };

  return (
    <div className="panel">
      <div className="panelTop">
        <div>
          <div className="h1">Addresses</div>
          <div className="p">Add delivery details for checkout.</div>
        </div>

        <div className="btnRow">
          <button className="soft" type="button" onClick={load}>
            Refresh
          </button>
          <button
            className="primarySm"
            type="button"
            onClick={() => setShowForm((v) => !v)}
          >
            {showForm ? "Close" : "Add address"}
          </button>
        </div>
      </div>

      {err ? <div className="alert">{err}</div> : null}

      {showForm ? (
        <div className="subPanel">
          <div className="h1">New Address</div>
          <div className="p">Fill these fields and save.</div>

          <div className="formGrid">
            <Field label="Label (Home / Work)">
              <input
                value={form.label}
                onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
                placeholder="Home"
              />
            </Field>

            <Field label="Full name">
              <input
                value={form.fullName}
                onChange={(e) => setForm((s) => ({ ...s, fullName: e.target.value }))}
                placeholder="Ama Mensah"
              />
            </Field>

            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
                placeholder="024xxxxxxx"
              />
            </Field>

            <Field label="Region">
              <input
                value={form.region}
                onChange={(e) => setForm((s) => ({ ...s, region: e.target.value }))}
                placeholder="Greater Accra"
              />
            </Field>

            <Field label="City">
              <input
                value={form.city}
                onChange={(e) => setForm((s) => ({ ...s, city: e.target.value }))}
                placeholder="Accra"
              />
            </Field>

            <Field label="Address line 1">
              <input
                value={form.addressLine1}
                onChange={(e) =>
                  setForm((s) => ({ ...s, addressLine1: e.target.value }))
                }
                placeholder="Osu Oxford Street"
              />
            </Field>

            <Field label="Address line 2 (optional)">
              <input
                value={form.addressLine2}
                onChange={(e) =>
                  setForm((s) => ({ ...s, addressLine2: e.target.value }))
                }
                placeholder="Near ____"
              />
            </Field>

            <Field label="Landmark (optional)">
              <input
                value={form.landmark}
                onChange={(e) =>
                  setForm((s) => ({ ...s, landmark: e.target.value }))
                }
                placeholder="Close to ____"
              />
            </Field>

            <label className="check">
              <input
                type="checkbox"
                checked={!!form.isDefault}
                onChange={(e) => setForm((s) => ({ ...s, isDefault: e.target.checked }))}
              />
              Make default address
            </label>
          </div>

          <div className="actions">
            <button className="primary" type="button" onClick={save}>
              Save address
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="empty">Loading addresses…</div>
      ) : !list.length ? (
        <div className="empty">📍 No addresses yet.</div>
      ) : (
        <div className="addrGrid">
          {list.map((a) => (
            <div className="addrCard" key={a.id}>
              <div className="addrTop">
                <div className="addrTitle">{safe(a.label) || "Address"}</div>
                {safe(profile?.defaultAddressId) === a.id ? (
                  <span className="badge bOk">Default</span>
                ) : null}
              </div>
              <div className="muted2">{safe(a.fullName) || "—"}</div>
              <div className="muted2">{safe(a.phone) || "—"}</div>
              <div className="addrLine">
                {safe(a.region)} • {safe(a.city)}
              </div>
              <div className="addrLine">{safe(a.addressLine1)}</div>
              {safe(a.addressLine2) ? (
                <div className="addrLine">{safe(a.addressLine2)}</div>
              ) : null}
              {safe(a.landmark) ? (
                <div className="addrLine muted2">Landmark: {safe(a.landmark)}</div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div className="field">
      <div className="infoLabel">{label}</div>
      {children}
    </div>
  );
}

/* ---------------- SUPPORT (FETCH SELLER) ---------------- */
function SupportPanel({ user, forcedStoreId }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [, setStoreId] = useState("");
  const [store, setStore] = useState(null);
  const [seller, setSeller] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    setStore(null);
    setSeller(null);

    const sid = await detectActiveStoreId(user, forcedStoreId);
    setStoreId(sid);

    if (!sid) {
      setLoading(false);
      return;
    }

    try {
      // 1) store
      const storeSnap = await getDoc(doc(db, "stores", sid));
      const storeData = storeSnap.exists()
        ? { id: storeSnap.id, ...storeSnap.data() }
        : null;
      setStore(storeData);

      // 2) seller fallback (more mappings)
      const ownerId =
        safe(storeData?.ownerId) ||
        safe(storeData?.sellerId) ||
        safe(storeData?.userId) ||
        safe(storeData?.uid) ||
        safe(storeData?.storeUid) ||
        safe(storeData?.createdBy) ||
        "";

      if (ownerId) {
        const userSnap = await getDoc(doc(db, "users", ownerId));
        const userData = userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null;
        setSeller(userData);
      }
    } catch (e) {
      setErr(e?.message || "Failed to load seller contact.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedStoreId]);

  const storeName = safe(store?.storeName) || "Seller";
  const waRaw = safe(store?.contactWhatsApp) || safe(store?.contactPhone) || safe(seller?.phone);
  const emailRaw = safe(store?.contactEmail) || safe(seller?.email);

  const waDigits = normalizePhoneForWhatsApp(waRaw);
  const waLink = waDigits ? `https://wa.me/${waDigits}` : "";
  const emailLink = emailRaw ? `mailto:${emailRaw}` : "";

  const noStoreSelected = !loading && !store && !seller;

  return (
    <div className="panel">
      <div className="h1">Support</div>
      <div className="p">Need help? Contact seller.</div>

      {err ? <div className="alert">{err}</div> : null}

      {loading ? (
        <div className="empty">Loading seller contact…</div>
      ) : noStoreSelected ? (
        <div className="empty">
          <b>No seller selected yet.</b>
          <div className="muted2" style={{ marginTop: 6 }}>
            Open a store then go to account using <b>/me?storeId=STORE_ID</b>
          </div>
        </div>
      ) : (
        <div className="supportHeader">
          <div className="supportTitleBig">{storeName}</div>
          <button className="soft" type="button" onClick={load}>
            Refresh seller
          </button>
        </div>
      )}

      {!loading && (store || seller) ? (
        <div className="supportGrid2">
          <div className="supportCard">
            <div className="supportTitle">WhatsApp</div>
            <div className="muted2">Chat directly with the seller.</div>

            {waLink ? (
              <a className="contactBtn" href={waLink} target="_blank" rel="noreferrer">
                Open WhatsApp
              </a>
            ) : (
              <div className="emptyMini">No WhatsApp number found for this seller.</div>
            )}

            {waRaw ? (
              <div className="muted2" style={{ marginTop: 10 }}>
                Number: {waRaw}
              </div>
            ) : null}
          </div>

          <div className="supportCard">
            <div className="supportTitle">Email</div>
            <div className="muted2">Send the seller an email.</div>

            {emailLink ? (
              <a className="contactBtn" href={emailLink}>
                Send Email
              </a>
            ) : (
              <div className="emptyMini">No email found for this seller.</div>
            )}

            {emailRaw ? (
              <div className="muted2" style={{ marginTop: 10 }}>
                Email: {emailRaw}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------- CSS ---------------- */
const css = `
:root{
  --bg:#f5f7fb;
  --paper:#ffffff;
  --ink:#0f172a;
  --muted:#64748b;
  --line:#e6e8f0;
  --brand:#2563eb;
  --brand2:#1d4ed8;
  --soft:#f1f5ff;
  --shadow:0 10px 25px rgba(15,23,42,.08);
  --shadow2:0 16px 40px rgba(15,23,42,.10);
}
*{box-sizing:border-box}
.bp{min-height:100vh;background:var(--bg);color:var(--ink);font-family:Inter,system-ui,Segoe UI,Arial}

/* topbar */
.top{position:sticky;top:0;z-index:30;background:rgba(255,255,255,.92);backdrop-filter:blur(10px);border-bottom:1px solid var(--line)}
.topInner{
  max-width:1200px;margin:0 auto;padding:12px 14px;
  display:grid;
  grid-template-columns:80px 120px 1fr 140px;
  align-items:center;
  gap:10px
}
.brand{display:flex;align-items:center;justify-content:center;gap:10px}
.dot{width:10px;height:10px;border-radius:999px;background:linear-gradient(135deg,var(--brand),#60a5fa)}
.brandText{font-weight:1100}
.topRight{display:flex;justify-content:flex-end}
.ghost{border:1px solid var(--line);background:var(--paper);padding:10px 12px;border-radius:12px;font-weight:900;cursor:pointer}
.ghost:hover{background:#f8fafc}
.danger{border:1px solid #fecaca;background:#fff;color:#dc2626;padding:10px 12px;border-radius:12px;font-weight:1000;cursor:pointer}
.danger:hover{background:#fff1f2}

/* hamburger only on mobile */
.ham{display:none}
@media (max-width:980px){
  .topInner{
    max-width:100%;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
    padding:10px 12px;
  }
  .ham{display:inline-flex;align-items:center;justify-content:center;width:44px;height:40px}
  .back{padding:10px 10px}
  .brand{justify-content:flex-start}
  .brandText{font-size:14px}
  .desktopSide{display:none}
  .topRight .danger{padding:10px 10px}
}

/* wrap */
.wrap{width:100%;padding:18px 14px 30px}
.wrapInner{max-width:1200px;margin:0 auto}
.layout{display:grid;grid-template-columns:320px 1fr;gap:14px;align-items:start}
@media (max-width:980px){
  .wrap{padding:10px}
  .wrapInner{width:100%;max-width:520px;margin:0 auto}
  .layout{grid-template-columns:1fr;gap:12px}
}

/* sidebar */
.sideCard{
  background:var(--paper);
  border:1px solid var(--line);
  border-radius:18px;
  box-shadow:var(--shadow2);
  padding:14px;
  position:sticky;
  top:72px;
}
.miniUser{display:flex;gap:12px;align-items:center;padding:8px 8px 12px;border-bottom:1px solid var(--line)}
.miniAvatar{
  width:44px;height:44px;border-radius:999px;overflow:hidden;
  background:linear-gradient(135deg,#a855f7,#ec4899);
  display:grid;place-items:center;color:#fff;font-weight:1100;
}
.miniAvatar img{width:100%;height:100%;object-fit:cover}
.miniName{font-weight:1100}
.miniSub{color:var(--muted);font-weight:800;font-size:12px;margin-top:2px;word-break:break-all}
.nav{display:grid;gap:8px;padding:12px 2px}
.sideItem{
  border:1px solid transparent;background:#fff;border-radius:14px;padding:12px 12px;
  display:flex;align-items:center;gap:10px;cursor:pointer;text-align:left;
}
.sideItem:hover{background:#f8fafc;border-color:var(--line)}
.sideItem.active{background:var(--soft);border-color:#dbeafe}
.sideIcon{width:24px;display:grid;place-items:center}
.sideLabel{font-weight:1000;flex:1}
.chev{color:var(--muted);font-weight:1200}
.logoutSide{
  width:100%;border:1px solid #fee2e2;background:#fff;color:#dc2626;
  border-radius:14px;padding:12px;font-weight:1100;cursor:pointer;
}
.logoutSide:hover{background:#fff1f2}

/* drawer */
.drawerWrap{position:fixed;inset:0;z-index:99}
.drawerOverlay{position:absolute;inset:0;background:rgba(15,23,42,.45)}
.drawer{
  position:absolute;left:0;top:0;bottom:0;
  width:min(88vw,360px);
  background:var(--paper);
  box-shadow:0 30px 80px rgba(0,0,0,.25);
  border-right:1px solid var(--line);
  padding:12px;
  animation:slideIn .18s ease-out;
}
@keyframes slideIn{from{transform:translateX(-10px);opacity:.6}to{transform:translateX(0);opacity:1}}
.drawerTop{display:flex;justify-content:space-between;align-items:center;padding:6px 6px 10px}
.drawerTitle{font-weight:1100}
.mobileCard{box-shadow:none;border-radius:16px}

/* main */
.main{display:grid;gap:12px}

/* hero */
.hero{
  background:linear-gradient(135deg, rgba(37,99,235,.10), rgba(99,102,241,.08));
  border:1px solid #dbeafe;border-radius:18px;padding:16px;
  display:flex;justify-content:space-between;align-items:center;gap:12px;
  box-shadow:var(--shadow);
}
.title{font-weight:1200;font-size:18px}
.subtitle{color:var(--muted);font-weight:800;margin-top:4px}
.primary{
  border:0;border-radius:14px;padding:12px 14px;
  background:linear-gradient(135deg,var(--brand),var(--brand2));
  color:#fff;font-weight:1100;cursor:pointer;
  box-shadow:0 10px 20px rgba(37,99,235,.18);
}
.primary:hover{filter:brightness(.98)}
@media (max-width:560px){
  .hero{flex-direction:column;align-items:stretch}
  .primary{width:100%}
  .title{font-size:16px}
}

/* panels */
.panel{
  background:var(--paper);border:1px solid var(--line);
  border-radius:18px;box-shadow:var(--shadow2);padding:16px;
}
.subPanel{
  margin-top:12px;
  border:1px solid var(--line);
  background:#fff;
  border-radius:18px;
  padding:14px;
}
.panelTop{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap}
.h1{font-weight:1200;font-size:16px}
.h2{font-weight:1200;font-size:18px;margin-top:10px}
.p{color:var(--muted);font-weight:800;margin-top:6px}
.pill{border:1px solid var(--line);background:#fff;padding:8px 10px;border-radius:999px;font-weight:1000;font-size:12px}
.pill.ok{border-color:#bbf7d0;background:#ecfdf5;color:#065f46}
.alert{margin-top:12px;border:1px solid #fecaca;background:#fff1f2;color:#991b1b;border-radius:14px;padding:10px 12px;font-weight:1000}
@media (max-width:560px){
  .panel{padding:14px}
  .hero{padding:14px}
}

/* grids */
.profileGrid{margin-top:12px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
@media (max-width:720px){ .profileGrid{grid-template-columns:1fr} }
.info{border:1px solid var(--line);background:#fff;border-radius:16px;padding:12px}
.infoLabel{color:var(--muted);font-weight:900;font-size:12px}
.infoValue{margin-top:6px;font-weight:1100;word-break:break-word}

.actions{margin-top:12px;display:flex;gap:10px;flex-wrap:wrap}
.soft{
  border:1px solid #dbeafe;background:var(--soft);color:#1e3a8a;
  padding:10px 12px;border-radius:14px;font-weight:1100;cursor:pointer;
}
.soft:hover{filter:brightness(.98)}

.empty{
  margin-top:12px;border:1px dashed #cbd5e1;background:#f8fafc;
  border-radius:16px;padding:16px;font-weight:1000;color:#334155;
}
.btnRow{display:flex;gap:10px;flex-wrap:wrap}

/* orders */
.orderGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-top:12px}
@media (max-width:720px){ .orderGrid{grid-template-columns:1fr} }
.orderCard{border:1px solid var(--line);border-radius:16px;padding:12px;background:#fff;box-shadow:var(--shadow)}
.orderTop{display:flex;justify-content:space-between;align-items:center;gap:10px}
.orderId{font-weight:1200}
.orderRow{display:flex;justify-content:space-between;gap:12px;margin-top:10px}
.muted2{color:var(--muted);font-weight:900;font-size:12px}
.val{font-weight:1100}

.orderActions{display:flex;gap:10px;margin-top:12px}
.primarySm{
  border:0;border-radius:12px;padding:10px 12px;
  background:linear-gradient(135deg,var(--brand),var(--brand2));
  color:#fff;font-weight:1100;cursor:pointer;
}
.softSm{
  border:1px solid #dbeafe;background:var(--soft);color:#1e3a8a;
  padding:10px 12px;border-radius:12px;font-weight:1100;cursor:pointer;
}
.softSm:disabled{opacity:.55;cursor:not-allowed}

.badge{padding:6px 10px;border-radius:999px;font-weight:1100;font-size:12px;border:1px solid var(--line);background:#fff}
.bNew{border-color:#bfdbfe;background:#eff6ff;color:#1d4ed8}
.bPending{border-color:#fde68a;background:#fffbeb;color:#92400e}
.bOk{border-color:#bbf7d0;background:#ecfdf5;color:#065f46}
.bDone{border-color:#c7d2fe;background:#eef2ff;color:#3730a3}
.bGray{border-color:#e5e7eb;background:#f9fafb;color:#374151}

.detailGrid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-top:12px}
@media (max-width:900px){ .detailGrid{grid-template-columns:repeat(2,minmax(0,1fr))} }
@media (max-width:560px){ .detailGrid{grid-template-columns:1fr} }
.detailBox{border:1px solid var(--line);border-radius:16px;padding:12px;background:#fff}

.itemsList{display:grid;gap:10px;margin-top:10px}
.itemRow{border:1px solid var(--line);border-radius:14px;padding:12px;background:#fff;display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap}
.itemTitle{font-weight:1100}
.itemMeta{display:flex;gap:10px;align-items:center;flex-wrap:wrap}

/* tracking */
.trackCard{margin-top:12px;border:1px solid var(--line);border-radius:18px;background:#fff;box-shadow:var(--shadow);padding:14px}

.barWrap{margin-top:14px}
.bar{height:10px;border-radius:999px;background:#eaf0ff;overflow:hidden;border:1px solid #dbeafe}
.barFill{height:100%;background:linear-gradient(135deg,var(--brand),var(--brand2))}
.barLabels{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:6px;margin-top:10px}
@media (max-width:720px){ .barLabels{grid-template-columns:1fr} }
.barLabel{font-size:11px;color:var(--muted);font-weight:900}
.barLabel.on{color:#1e3a8a}

.timeline{margin-top:14px}
.timelineList{display:grid;gap:12px;margin-top:10px}
.timeRow{display:flex;gap:10px;align-items:flex-start}
.dot2{width:10px;height:10px;border-radius:999px;background:linear-gradient(135deg,var(--brand),#60a5fa);margin-top:6px}
.timeTitle{font-weight:1100}

/* saved */
.savedGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-top:12px}
@media (max-width:720px){ .savedGrid{grid-template-columns:1fr} }
.savedCard{border:1px solid var(--line);border-radius:16px;padding:12px;background:#fff;box-shadow:var(--shadow)}
.savedTop{display:flex;gap:12px;align-items:center}
.savedImg{
  width:86px;height:86px;border-radius:14px;overflow:hidden;
  border:1px solid var(--line);
  background:#f8fafc;
  display:grid;place-items:center;
  flex:0 0 auto;
}
.savedImg img{width:100%;height:100%;object-fit:cover}
.imgPh{font-weight:1000;color:#64748b;font-size:12px;padding:10px;text-align:center}
.savedInfo{flex:1;min-width:0}
.savedTitle{font-weight:1200}
.savedBottom{display:flex;justify-content:flex-end;align-items:center;gap:10px;margin-top:12px}
.dangerSm{border:1px solid #fecaca;background:#fff;color:#dc2626;padding:10px 12px;border-radius:12px;font-weight:1100;cursor:pointer}
.dangerSm:hover{background:#fff1f2}

/* addresses */
.addrGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-top:12px}
@media (max-width:720px){ .addrGrid{grid-template-columns:1fr} }
.addrCard{border:1px solid var(--line);border-radius:16px;padding:12px;background:#fff;box-shadow:var(--shadow)}
.addrTop{display:flex;justify-content:space-between;align-items:center;gap:10px}
.addrTitle{font-weight:1200}
.addrLine{margin-top:6px;font-weight:900;color:#334155}

.formGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-top:12px}
@media (max-width:720px){ .formGrid{grid-template-columns:1fr} }
.field input{
  width:100%;
  margin-top:6px;
  padding:10px 12px;
  border:1px solid var(--line);
  border-radius:12px;
  outline:none;
  font-weight:900;
}
.check{margin-top:10px;display:flex;gap:10px;align-items:center;font-weight:1000;color:#0f172a}

/* support */
.supportHeader{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-top:12px;flex-wrap:wrap}
.supportTitleBig{font-weight:1200}
.supportGrid2{margin-top:12px;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
@media (max-width:900px){ .supportGrid2{grid-template-columns:1fr} }
.supportCard{border:1px solid var(--line);border-radius:16px;padding:12px;background:#fff;box-shadow:var(--shadow)}
.supportTitle{font-weight:1200}
.emptyMini{margin-top:10px;border:1px dashed #cbd5e1;background:#f8fafc;border-radius:14px;padding:12px;font-weight:1000;color:#334155}
.contactBtn{
  display:inline-flex;
  margin-top:10px;
  padding:10px 12px;
  border-radius:12px;
  text-decoration:none;
  font-weight:1100;
  border:1px solid #dbeafe;
  background:var(--soft);
  color:#1e3a8a;
}
.contactBtn:hover{filter:brightness(.98)}
`;
